import { Badge, Button, DataGrid, TooltipV2 } from '@cimplico/components';
import { useViewLink } from '../../queries/use-view-link';
import { FileRecordNew, FileRecordTypeEnum } from '../../types/file-record';
import { calcRelativeTime, formatDateTime } from '../../utilities/formatting';
import { useDispatch } from 'react-redux';
import { setViewingDocument } from '../../slices/documentViewSlice';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { FYI_URL } from '../../config';
import { useCreateDocument } from '../../queries/use-create-document';
import { toastNotification } from '../../components/toast/Toast';
import { useIsActiveSubscription } from '../../hooks/use-is-active-subscription';
import RecordTypeIcon from './RecordTypeIcon';
import Dropzone from '../../components/dropzone/Dropzone';
import FilesBreadcrumbs from './FilesBreadcrumbs';
import FileStatusIcon from './FileStatusIcon';
import { useFileRecord } from '../../queries/use-file-record';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { isFolderType } from './file-utils';
import FileName from './FileName';

type FileRowProps = {
    id: string;
    readOnly?: boolean;
    showPath?: boolean;
    record: FileRecordNew;
    isReferenced?: boolean;
    onUploading: (uploading: boolean) => void;
    onDetailsToggle: (id: string) => void;
    onFolderSelect: (id: string) => void;
};

function FileRow({
    id,
    readOnly = false,
    record,
    isReferenced = false,
    onUploading,
    onDetailsToggle,
    onFolderSelect,
    showPath = false,
}: FileRowProps): JSX.Element {
    const fileLimit = 30;

    const createDocumentQuery = useCreateDocument();

    const isFolder = isFolderType(record.recordType);

    async function handleFileUpload(files: File[]): Promise<void> {
        onUploading(true);
        const uploads = [];
        for (const file of files) {
            const uploadPromise = createDocumentQuery.mutateAsync(
                { file, parentFolderId: record.id },
                {
                    onSuccess: () => {
                        toastNotification('success', 'New file uploaded!');
                    },
                }
            );
            uploads.push(uploadPromise);
        }
        await Promise.allSettled(uploads);
        onUploading(false);
    }

    return (
        <Dropzone onDrop={handleFileUpload} disabled={readOnly || !isFolder} fileLimit={fileLimit}>
            <DataGrid.Row id={id} disabled={isFolder}>
                <RecordTypeIcon
                    recordType={record.link ? FileRecordTypeEnum.LINK : record.recordType}
                    system={record.systemRecord}
                    locked={record.locked}
                />
                <div className="group flex gap-1 items-center">
                    <div className="py-1">
                        {showPath && (
                            <div className="mb-1">
                                <FilesBreadcrumbs
                                    path={record.path.slice(0, record.path.length - 1)}
                                    onLevelSelect={onFolderSelect}
                                    size="small"
                                    disableCurrentLevel={false}
                                />
                            </div>
                        )}
                        <div className="flex items-start gap-2">
                            <FileStatusIcon status={record.status} />
                            <FileName record={record} onFolderSelect={onFolderSelect} />
                            {isReferenced && <Badge color="green" shape="squared" size="small" label="Referenced" />}
                        </div>
                    </div>
                </div>
                <p className="text-sm text-gray-700">
                    {record.itemCount !== undefined && record.itemCount !== null ? record.itemCount : '-'}
                </p>
                <TooltipV2 message={formatDateTime(record.updatedAt)}>
                    <p className="text-gray-700 text-sm">{calcRelativeTime(record.updatedAt)}</p>
                </TooltipV2>
                <Button
                    type="hollow"
                    size="md"
                    leftIcon={<InformationCircleIcon />}
                    onClick={(): void => {
                        onDetailsToggle(record.id);
                    }}
                />
            </DataGrid.Row>
        </Dropzone>
    );
}

export default FileRow;
